import { Dialog, Transition } from '@headlessui/react';
import { formatNPR, formatNPRRange } from '../../utils/currency';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addByIncrement } from '../../store/reducers/cartSlice';
import useFetch from '../../utils/useFetch';
import { getMoqFromInventory, normalizeOrderQuantity } from '../../utils/orderQuantity';
import {
  autoCompleteSelection,
  findMatchingInventories,
  getAttributeKeys,
  buildGalleryImageInventoryMap,
  getAllProductGalleryImages,
  getGalleryIndicesForSelection,
  getInitialSelection,
  getSelectionForGalleryIndex,
  getVariantLabelForGalleryIndex,
  isSelectionComplete,
} from '../../utils/productVariants';
import OrderQuantitySelector from '../orderQuantity/OrderQuantitySelector';
import ProductVariantPicker from '../product/ProductVariantPicker';
import ProductGalleryPremium from '../product/ProductGalleryPremium';

export default function Modal({ isOpen, closeModal, slug }) {
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [filteredInventory, setFilteredInventory] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [total, setTotal] = useState(0);
  const [gallerySyncToken, setGallerySyncToken] = useState(0);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  const Api = `inventory/products`;
  const dispatch = useDispatch();
  const { data, loading, error } = useFetch(`${Api}/${slug}/`);

  const selectedInventory =
    filteredInventory?.length > 0 ? filteredInventory[0] : null;

  useEffect(() => {
    if (data?.inventories_details?.length > 0) {
      const keys = getAttributeKeys(data.inventories_details, data.attributes);
      const initial = autoCompleteSelection(
        data.inventories_details,
        getInitialSelection(data.inventories_details),
        keys
      );
      setSelectedAttributes(initial);
      setGallerySyncToken((t) => t + 1);
    }
  }, [data]);

  useEffect(() => {
    if (filteredInventory?.[0]) {
      setTotal(getMoqFromInventory(filteredInventory[0], data));
    }
  }, [filteredInventory, data]);

  useEffect(() => {
    if (data?.inventories_details) {
      const keys = getAttributeKeys(data.inventories_details, data.attributes);
      if (isSelectionComplete(selectedAttributes, keys)) {
        const filtered = findMatchingInventories(
          data.inventories_details,
          selectedAttributes
        );
        setFilteredInventory(filtered.length > 0 ? filtered : null);
      } else {
        setFilteredInventory(null);
      }
    }
  }, [selectedAttributes, data]);

  const handleAttributeChange = (nextSelection) => {
    setSelectedAttributes(nextSelection);
    setGallerySyncToken((t) => t + 1);
    const imgs = getAllProductGalleryImages(data, data?.inventories_details);
    const { focusIndex } = getGalleryIndicesForSelection(
      imgs,
      data?.inventories_details,
      nextSelection
    );
    setActiveGalleryIndex(focusIndex);
  };

  const galleryImages = getAllProductGalleryImages(data, data?.inventories_details);

  const galleryImageMap = useMemo(
    () => buildGalleryImageInventoryMap(data, data?.inventories_details),
    [data]
  );

  const { focusIndex: galleryFocusIndex } = getGalleryIndicesForSelection(
    galleryImages,
    data?.inventories_details,
    selectedAttributes
  );

  const activeVariantLabel =
    galleryImageMap[activeGalleryIndex]?.label ||
    getVariantLabelForGalleryIndex(
      galleryImages,
      data?.inventories_details,
      activeGalleryIndex,
      data
    );

  const handleGalleryImageChange = useCallback(
    (index) => {
      setActiveGalleryIndex(index);
      const slide = galleryImageMap[index];
      if (slide?.selection) {
        setSelectedAttributes({ ...slide.selection });
        return;
      }
      const next = getSelectionForGalleryIndex(
        galleryImages,
        data?.inventories_details,
        index,
        data
      );
      if (next) setSelectedAttributes({ ...next });
    },
    [galleryImageMap, galleryImages, data]
  );

  const handleAddToCart = (inventory) => {
    if (inventory?.length > 0) {
      const product = inventory[0];
      const moq = getMoqFromInventory(product, data);
      const maxQty = product.stock;
      const quantity = normalizeOrderQuantity(total, {
        step: moq,
        min: moq,
        max: maxQty,
      });
      setTotal(quantity);
      dispatch(addByIncrement({ product, cartQuantity: quantity }));
      closeModal();
    }
  };

  const priceSource =
    filteredInventory?.length > 0
      ? filteredInventory
      : findMatchingInventories(data?.inventories_details || [], selectedAttributes);

  const displayPrice =
    priceSource.length > 0
      ? priceSource.length > 1
        ? formatNPRRange(Math.min(...priceSource.map((i) => parseFloat(i.price))), Math.max(...priceSource.map((i) => parseFloat(i.price))))
        : formatNPR(priceSource[0].price)
      : data?.properties?.price_range
        ? formatNPRRange(data.properties.price_range.lowest_price, data.properties.price_range.highest_price)
        : null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl max-h-[92vh] sm:max-h-[88vh] flex flex-col bg-white sm:rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-4 py-3 sm:px-5 border-b border-gray-100 shrink-0">
                  <div className="min-w-0 pr-8">
                    <Dialog.Title as="h2" className="text-base sm:text-lg font-bold text-gray-900 capitalize truncate">
                      <Link
                        to={`/product/${data?.slug}`}
                        onClick={closeModal}
                        className="hover:text-brand-600 transition-colors"
                      >
                        {data?.name || 'Product'}
                      </Link>
                    </Dialog.Title>
                    {displayPrice && (
                      <p className="text-brand-600 font-bold text-lg sm:text-xl mt-0.5">{displayPrice}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {loading ? (
                  <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
                ) : error ? (
                  <div className="p-8 text-center text-red-600 text-sm">{error.message}</div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto overscroll-contain">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-0">
                        {/* Image */}
                        <div className="relative bg-gray-50 md:border-r border-gray-100 p-2 sm:p-3">
                          <ProductGalleryPremium
                            key={data?.id}
                            images={galleryImages}
                            focusIndex={galleryFocusIndex}
                            syncToken={gallerySyncToken}
                            activeVariantLabel={activeVariantLabel}
                            onActiveImageChange={handleGalleryImageChange}
                          />
                          {data?.category_details?.name && (
                            <span className="absolute bottom-3 left-3 text-xs font-medium bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-gray-700 shadow-sm">
                              {data.category_details.name}
                            </span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="p-4 sm:p-5 space-y-4">
                          {data?.description && (
                            <div>
                              <p
                                className={`text-sm text-gray-600 leading-relaxed ${
                                  !isExpanded ? 'line-clamp-2' : ''
                                }`}
                              >
                                {data.description}
                              </p>
                              {data.description.length > 120 && (
                                <button
                                  type="button"
                                  onClick={() => setIsExpanded(!isExpanded)}
                                  className="text-xs font-semibold text-brand-600 mt-1"
                                >
                                  {isExpanded ? 'Show less' : 'Read more'}
                                </button>
                              )}
                            </div>
                          )}

                          <ProductVariantPicker
                            compact
                            attributes={data?.attributes}
                            inventories={data?.inventories_details}
                            selectedAttributes={selectedAttributes}
                            onChange={handleAttributeChange}
                          />

                          {selectedInventory ? (
                            <div className="space-y-3 pt-1">
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-gray-500">
                                  SKU <span className="font-mono text-gray-800">{selectedInventory.sku}</span>
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded-full font-medium ${
                                    selectedInventory.stock > 0
                                      ? 'bg-brand-100 text-brand-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {selectedInventory.stock > 0 ? 'In stock' : 'Out of stock'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
                              Choose all options above. Quantity is sold in{' '}
                              <strong>bundles</strong> — 1 bundle = minimum order (pieces per bundle).
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Sticky footer — purchase */}
                    {selectedInventory && (
                      <div className="shrink-0 border-t border-gray-100 bg-gray-50/95 backdrop-blur px-4 py-3 sm:px-5 sm:py-4">
                        <OrderQuantitySelector
                          variant="modal"
                          moq={getMoqFromInventory(selectedInventory, data)}
                          total={total}
                          onChange={setTotal}
                          maxQty={selectedInventory.stock}
                          availableStock={selectedInventory.stock}
                          onAddToCart={() => handleAddToCart(filteredInventory)}
                        />
                      </div>
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
